// Phase 11d — caregiver-self payout views (`/me/caregiver/payouts*`).
//
// Per 11d-2: DRAFT payouts are hidden from the caregiver. The visible
// status set is ISSUED / PAID / VOIDED — same enum as Prisma's
// PayoutStatus but with DRAFT excluded at the API boundary.
//
// Per 11d-4: detail shape = header + lines + signedPdfUrl; PaymentEvent
// history excluded (operator-facing detail). Buyer label anonymized at
// the line layer ("Facility — Sunrise LTC", "Homecare — J.D.") so the
// caregiver knows the context without seeing the buyer's full identity.
//
// Per 11d-5: signed PDF URL is generated with a 5-minute TTL each time
// the detail endpoint is called; frontend uses it immediately or refetches.
import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────────────────
// Common pieces
// ─────────────────────────────────────────────────────────────────────────────
const IsoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
/** Caregiver-visible payout statuses — DRAFT excluded per 11d-2. */
export const CaregiverPayoutStatusSelfSchema = z.enum(['ISSUED', 'PAID', 'VOIDED']);
/** Caregiver-visible mechanic — bank-agnostic per 11a-7. */
export const CaregiverPayoutMechanicSelfSchema = z.enum([
    'EFT',
    'INTERAC',
    'CHEQUE_MAILED',
    'MANUAL',
]);
/** Caregiver-visible payout line type — same enum as Prisma's PayoutLineType. */
export const CaregiverPayoutLineTypeSelfSchema = z.enum([
    'SHIFT_PAY',
    'STAT_HOLIDAY_PAY',
    'CANCELLATION_PAY',
    'BONUS',
    'MANUAL_ADJUSTMENT',
]);
// ─────────────────────────────────────────────────────────────────────────────
// GET /me/caregiver/payouts — list query + response
// ─────────────────────────────────────────────────────────────────────────────
/** Query params per 11d-3:
 *   - `year` defaults to current calendar year; the controller resolves
 *     "current" at request time.
 *   - `from` + `to` override the year filter when both are present.
 *   - page + pageSize cap at 100 with default 20.
 *
 *   The year-vs-period choice is taken at the service layer; both
 *   resolve to a WHERE clause on `cycleStartDate`.
 */
export const CaregiverPayoutsSelfListQuerySchema = z
    .object({
    year: z.coerce.number().int().min(2000).max(2200).optional(),
    from: IsoDateSchema.optional(),
    to: IsoDateSchema.optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
})
    .refine((v) => (v.from === undefined) === (v.to === undefined), {
    message: 'from and to must be provided together (or both omitted)',
    path: ['from'],
});
export const CaregiverPayoutSelfListItemSchema = z.object({
    id: z.string().uuid(),
    payoutNumber: z.string(),
    status: CaregiverPayoutStatusSelfSchema,
    subtotalCents: z.number().int(),
    hstCents: z.number().int(),
    totalCents: z.number().int(),
    cycleStartDate: IsoDateSchema,
    cycleEndDate: IsoDateSchema,
    issuedAt: z.string().datetime(),
    holdUntil: z.string().datetime().nullable(),
    paidAt: z.string().datetime().nullable(),
});
export const CaregiverPayoutsSelfListResponseSchema = z.object({
    items: z.array(CaregiverPayoutSelfListItemSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// ─────────────────────────────────────────────────────────────────────────────
// GET /me/caregiver/payouts/:id — detail response
// ─────────────────────────────────────────────────────────────────────────────
export const CaregiverPayoutLineSelfSchema = z.object({
    id: z.string().uuid(),
    lineType: CaregiverPayoutLineTypeSelfSchema,
    description: z.string(),
    /** Decimal-string preserve-precision per the billing-cents convention. */
    quantityHours: z.string(),
    unitPayRate: z.string(),
    subtotalCents: z.number().int(),
    /** Anonymized per 11d-4. Null for BONUS / MANUAL_ADJUSTMENT (no shift FK). */
    buyerLabel: z.string().nullable(),
    /** ISO date of the shift's scheduledStart; null for BONUS / MANUAL_ADJUSTMENT. */
    shiftDate: IsoDateSchema.nullable(),
});
export const CaregiverPayoutSelfDetailResponseSchema = z.object({
    id: z.string().uuid(),
    payoutNumber: z.string(),
    status: CaregiverPayoutStatusSelfSchema,
    subtotalCents: z.number().int(),
    hstCents: z.number().int(),
    totalCents: z.number().int(),
    cycleStartDate: IsoDateSchema,
    cycleEndDate: IsoDateSchema,
    mechanic: CaregiverPayoutMechanicSelfSchema.nullable(),
    externalRef: z.string().nullable(),
    issuedAt: z.string().datetime(),
    holdUntil: z.string().datetime().nullable(),
    paidAt: z.string().datetime().nullable(),
    voidedAt: z.string().datetime().nullable(),
    voidReason: z.string().nullable(),
    hstRegistrantAtIssue: z.boolean(),
    hstNumberAtIssue: z.string().nullable(),
    /** Pre-signed DO Spaces GET URL per 11d-5; 5-minute TTL. Null if the
     *  statement PDF hasn't been rendered yet (DRAFT-only state is hidden
     *  per 11d-2, but freshly-ISSUED rows may transit a brief window before
     *  Phase 11e's render job completes). */
    statementPdfDownloadUrl: z.string().url().nullable(),
    lines: z.array(CaregiverPayoutLineSelfSchema),
});
//# sourceMappingURL=payouts-self.schema.js.map